/**
 * FeedReactions — Iter-297c — Inline emoji reactions bar.
 *
 * Previous (Iter-294) iteration rendered a "Like" primary toggle button
 * plus a hover/long-press picker. Users complained that the primary
 * "👍 Like" chip looked identical to the legacy Like button and the
 * full emoji bar was hidden behind hover, defeating the point of the
 * redesign.
 *
 * Iter-297c — show the six core reactions inline by default (Like,
 * Love, Haha, Wow, Sad, Angry — matches Facebook's canonical set). The
 * user's current reaction is highlighted with a tinted background ring.
 * Click any emoji to set / replace; click the same one again to remove.
 * The "who reacted" popover is unchanged.
 *
 * Backend contract:
 *   GET  /api/feed/{id}/reactions      → { like, love, ..., user_reactions: [] }
 *   POST /api/feed/{id}/react          → toggle / set reaction
 *   GET  /api/feed/{id}/reactors       → list of reactor profiles
 *
 * We use native Unicode emoji so the platform's own emoji font
 * (Apple Color Emoji, Noto Color Emoji, Segoe UI Emoji, etc.) supplies
 * the full-color glyph — no Twemoji sprite sheet dependency.
 */
import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';

// Order matches REACTION_TYPES in /app/backend/routes/feed_routes.py.
// Keep these in sync — if you reorder, reorder there too.
// Iter-297c — surface the 6 canonical reactions; the legacy
// celebrate/applause/fire still POST through fine but no longer take
// up space in the primary bar.
const REACTIONS = [
  { type: 'like',      emoji: '👍', label: 'Like',  activeColor: 'text-blue-600',   activeBg: 'bg-blue-50  ring-blue-200' },
  { type: 'love',      emoji: '❤️', label: 'Love',  activeColor: 'text-red-500',    activeBg: 'bg-red-50   ring-red-200' },
  { type: 'laugh',     emoji: '😂', label: 'Haha',  activeColor: 'text-amber-500',  activeBg: 'bg-amber-50 ring-amber-200' },
  { type: 'wow',       emoji: '😮', label: 'Wow',   activeColor: 'text-amber-500',  activeBg: 'bg-amber-50 ring-amber-200' },
  { type: 'sad',       emoji: '😢', label: 'Sad',   activeColor: 'text-sky-500',    activeBg: 'bg-sky-50   ring-sky-200' },
  { type: 'angry',     emoji: '😡', label: 'Angry', activeColor: 'text-orange-600', activeBg: 'bg-orange-50 ring-orange-200' },
];
const LEGACY_REACTIONS = [
  { type: 'celebrate', emoji: '🎉', label: 'Celebrate', activeColor: 'text-pink-500',   activeBg: 'bg-pink-50 ring-pink-200' },
  { type: 'applause',  emoji: '👏', label: 'Applause',  activeColor: 'text-amber-500',  activeBg: 'bg-amber-50 ring-amber-200' },
  { type: 'fire',      emoji: '🔥', label: 'Fire',      activeColor: 'text-orange-500', activeBg: 'bg-orange-50 ring-orange-200' },
];
const REACTION_BY_TYPE = [...REACTIONS, ...LEGACY_REACTIONS].reduce(
  (acc, r) => ({ ...acc, [r.type]: r }), {},
);

export function FeedReactions({ postId, reactions, onUpdate }) {
  const [pending, setPending] = useState(false);
  const [pendingType, setPendingType] = useState(null);
  const [reactorsOpen, setReactorsOpen] = useState(false);
  const [reactors, setReactors] = useState([]);
  const [reactorsLoading, setReactorsLoading] = useState(false);

  // Backend always returns user_reactions as an array, but with the
  // V295 single-reaction-per-user constraint it now contains 0 or 1
  // entries. The legacy `.includes(type)` API still works.
  const userReactions = reactions?.user_reactions || [];
  const currentReaction = userReactions[0] || null;

  const containerRef = useRef(null);

  // Close reactors popover on click-outside.
  useEffect(() => {
    if (!reactorsOpen) return;
    const onDocPointer = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setReactorsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
    };
  }, [reactorsOpen]);

  const submitReaction = async (type) => {
    if (pending) return;
    setPending(true);
    setPendingType(type);
    try {
      const res = await api.post(`/feed/${postId}/react`, { reaction_type: type });
      if (onUpdate) onUpdate(postId, res.data.reactions);
    } catch {
      /* silent — UI stays in its previous state */
    } finally {
      setPending(false);
      setPendingType(null);
    }
  };

  const loadReactors = async () => {
    setReactorsLoading(true);
    try {
      const res = await api.get(`/feed/${postId}/reactors`, { params: { limit: 50 } });
      setReactors(res.data?.reactors || []);
    } catch {
      setReactors([]);
    } finally {
      setReactorsLoading(false);
    }
  };

  const toggleReactors = () => {
    if (!reactorsOpen) loadReactors();
    setReactorsOpen((v) => !v);
  };

  // ── Summary chips (top 3 emojis + total count) ───────────────────
  const summary = [...REACTIONS, ...LEGACY_REACTIONS]
    .map((r) => ({ ...r, count: reactions?.[r.type] || 0 }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
  const totalCount = summary.reduce((sum, r) => sum + r.count, 0);
  const topThree = summary.slice(0, 3);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center gap-1.5 flex-wrap"
      data-testid={`reactions-${postId}`}
      data-feed-reaction-trigger="true"
    >
      {/* Inline emoji bar — always visible. Tap any emoji to set or
          toggle. The currently-active reaction gets a tinted ring. */}
      {REACTIONS.map((r) => {
        const isCurrent = currentReaction === r.type;
        const isLoading = pending && pendingType === r.type;
        return (
          <button
            key={r.type}
            type="button"
            onClick={() => submitReaction(r.type)}
            disabled={pending}
            data-testid={`react-${r.type}-${postId}`}
            title={r.label}
            aria-label={`React with ${r.label}${isCurrent ? ' (currently selected — tap again to remove)' : ''}`}
            aria-pressed={isCurrent}
            className={`flex items-center justify-center w-9 h-9 rounded-full text-xl leading-none transition-all duration-150 ease-out select-none
              ${isCurrent
                ? `${r.activeBg} ring-2`
                : 'hover:bg-gray-100 active:bg-gray-200'}
              hover:scale-110 active:scale-95
              ${pending && !isLoading ? 'opacity-40' : ''}
              ${isLoading ? 'animate-pulse' : ''}`}
          >
            <span aria-hidden>{r.emoji}</span>
          </button>
        );
      })}

      {/* Reaction summary chip (top 3 emojis + total). */}
      {totalCount > 0 && (
        <button
          type="button"
          onClick={toggleReactors}
          data-testid={`reactions-summary-${postId}`}
          className="flex items-center gap-1 px-2 py-1 rounded-full text-xs text-gray-600 hover:bg-gray-100"
          aria-label={`${totalCount} reaction${totalCount === 1 ? '' : 's'} — view who reacted`}
        >
          <span className="flex -space-x-1" aria-hidden>
            {topThree.map((r) => (
              <span
                key={r.type}
                className="text-base leading-none select-none drop-shadow-sm"
                title={r.label}
              >
                {r.emoji}
              </span>
            ))}
          </span>
          <span className="font-medium">{totalCount}</span>
        </button>
      )}

      {/* "Who reacted" popover. */}
      {reactorsOpen && (
        <div
          className="absolute top-full left-0 mt-2 w-72 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-20"
          data-testid={`reactors-popover-${postId}`}
        >
          <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">
              {totalCount} reaction{totalCount === 1 ? '' : 's'}
            </p>
            <button
              type="button"
              onClick={() => setReactorsOpen(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
              aria-label="Close reactors"
              data-testid={`reactors-close-${postId}`}
            >
              Close
            </button>
          </div>
          {reactorsLoading ? (
            <div className="p-4 text-center text-xs text-gray-500">Loading…</div>
          ) : reactors.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-500">No reactions yet.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {reactors.map((r) => {
                const meta = REACTION_BY_TYPE[r.reaction_type];
                return (
                  <li key={r.user_id} className="flex items-center gap-3 px-3 py-2">
                    {r.avatar_url ? (
                      <img
                        src={r.avatar_url}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover bg-gray-100"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                        {(r.display_name || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {r.display_name || 'Unknown'}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-gray-500">
                        {r.account_role}
                      </p>
                    </div>
                    {meta && (
                      <span className="text-lg leading-none" title={meta.label} aria-hidden>
                        {meta.emoji}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Also export the constants so other components can render counts /
// summaries without re-declaring them.
export { REACTIONS, REACTION_BY_TYPE };
